/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NumberInput, ResultBox } from './parts';

// ---------------- eGFR (CKD-EPI 2021 semplificata) ----------------
export const EgfrCalc = () => {
  const [creat, setCreat] = useState(null);
  const [age, setAge] = useState(null);
  const [sex, setSex] = useState('M');
  const compute = () => {
    if (!creat || !age) return null;
    const k = sex === 'F' ? 0.7 : 0.9;
    const alpha = sex === 'F' ? -0.241 : -0.302;
    const minTerm = Math.min(creat / k, 1) ** alpha;
    const maxTerm = Math.max(creat / k, 1) ** -1.2;
    const sexFactor = sex === 'F' ? 1.012 : 1.0;
    return 142 * minTerm * maxTerm * (0.9938 ** age) * sexFactor;
  };
  const gfr = compute();
  let tone = 'neutral';
  let interp = null;
  if (gfr !== null && !Number.isNaN(gfr)) {
    if (gfr >= 90) { tone = 'good'; interp = 'G1 — Normale o elevata'; }
    else if (gfr >= 60) { tone = 'good'; interp = 'G2 — Lieve riduzione'; }
    else if (gfr >= 45) { tone = 'warning'; interp = 'G3a — Riduzione lieve-moderata'; }
    else if (gfr >= 30) { tone = 'warning'; interp = 'G3b — Riduzione moderata-severa'; }
    else if (gfr >= 15) { tone = 'danger'; interp = 'G4 — Riduzione severa'; }
    else { tone = 'danger'; interp = 'G5 — Insufficienza renale terminale'; }
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <NumberInput id="egfr-cr" label="Creatinina s." unit="mg/dL" value={creat} onChange={setCreat} step={0.01} min={0.1} />
        <NumberInput id="egfr-age" label="Eta" unit="anni" value={age} onChange={setAge} min={18} max={120} />
        <div className="space-y-1.5">
          <Label className="text-xs">Sesso</Label>
          <Select value={sex} onValueChange={setSex}>
            <SelectTrigger data-testid="calc-input-egfr-sex"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="M">Maschio</SelectItem>
              <SelectItem value="F">Femmina</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <ResultBox value={gfr} unit="mL/min/1.73m²" interpretation={interp} tone={tone} />
      <p className="text-[11px] text-muted-foreground">CKD-EPI 2021 (senza fattore razziale)</p>
    </div>
  );
};

// ---------------- CHA2DS2-VASc ----------------
export const Cha2ds2Calc = () => {
  const [state, setState] = useState({
    chf: false, htn: false, age75: false, dm: false, stroke: false,
    vasc: false, age65: false, female: false,
  });
  const toggle = (k) => setState((s) => ({ ...s, [k]: !s[k] }));
  const score =
    (state.chf ? 1 : 0) + (state.htn ? 1 : 0) + (state.age75 ? 2 : 0) +
    (state.dm ? 1 : 0) + (state.stroke ? 2 : 0) + (state.vasc ? 1 : 0) +
    (state.age65 && !state.age75 ? 1 : 0) + (state.female ? 1 : 0);
  const items = [
    { k: 'chf', label: 'Scompenso cardiaco (+1)' },
    { k: 'htn', label: 'Ipertensione (+1)' },
    { k: 'age75', label: 'Eta >= 75 anni (+2)' },
    { k: 'dm', label: 'Diabete (+1)' },
    { k: 'stroke', label: 'TIA/Ictus pregresso (+2)' },
    { k: 'vasc', label: 'Patologia vascolare (+1)' },
    { k: 'age65', label: 'Eta 65-74 (+1)' },
    { k: 'female', label: 'Sesso femminile (+1)' },
  ];
  let tone = 'good';
  let interp = 'Basso rischio — no anticoagulazione (o valutare)';
  if (score >= 2) { tone = 'danger'; interp = 'Alto rischio — anticoagulazione raccomandata'; }
  else if (score === 1) { tone = 'warning'; interp = 'Rischio intermedio — valutare anticoagulazione'; }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {items.map((it) => (
          <button
            key={it.k}
            onClick={() => toggle(it.k)}
            className={`text-left rounded-lg border p-2 text-xs transition-colors ${state[it.k] ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:border-primary/40'}`}
            data-testid={`cha2-${it.k}`}
          >
            {state[it.k] ? '✓ ' : ''}{it.label}
          </button>
        ))}
      </div>
      <ResultBox value={score} unit="punti" interpretation={interp} tone={tone} />
    </div>
  );
};

// ---------------- Wells score (embolia polmonare) ----------------
export const WellsPeCalc = () => {
  const [state, setState] = useState({
    clinical: false, altDx: false, tachy: false, immob: false,
    prevPeDvt: false, hemop: false, cancer: false,
  });
  const toggle = (k) => setState((s) => ({ ...s, [k]: !s[k] }));
  const score =
    (state.clinical ? 3 : 0) + (state.altDx ? 3 : 0) + (state.tachy ? 1.5 : 0) +
    (state.immob ? 1.5 : 0) + (state.prevPeDvt ? 1.5 : 0) + (state.hemop ? 1 : 0) +
    (state.cancer ? 1 : 0);
  const items = [
    { k: 'clinical', label: 'Segni/sintomi di TVP (+3)' },
    { k: 'altDx', label: 'EP piu probabile di altre dx (+3)' },
    { k: 'tachy', label: 'FC > 100 bpm (+1.5)' },
    { k: 'immob', label: 'Immobilizzazione > 3g o chirurgia < 4 sett (+1.5)' },
    { k: 'prevPeDvt', label: 'Pregressa EP/TVP (+1.5)' },
    { k: 'hemop', label: 'Emottisi (+1)' },
    { k: 'cancer', label: 'Neoplasia attiva (+1)' },
  ];
  let tone = 'good';
  let interp = 'Rischio basso — considera D-dimero';
  if (score > 6) { tone = 'danger'; interp = 'Rischio ALTO — angio-TC torace'; }
  else if (score >= 2) { tone = 'warning'; interp = 'Rischio moderato — D-dimero / imaging'; }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2">
        {items.map((it) => (
          <button
            key={it.k}
            onClick={() => toggle(it.k)}
            className={`text-left rounded-lg border p-2 text-xs transition-colors ${state[it.k] ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:border-primary/40'}`}
            data-testid={`wells-${it.k}`}
          >
            {state[it.k] ? '✓ ' : ''}{it.label}
          </button>
        ))}
      </div>
      <ResultBox value={score} unit="punti" interpretation={interp} tone={tone} />
    </div>
  );
};

// ---------------- CURB-65 ----------------
export const Curb65Calc = () => {
  const [state, setState] = useState({ conf: false, urea: false, rr: false, bp: false, age: false });
  const toggle = (k) => setState((s) => ({ ...s, [k]: !s[k] }));
  const score = Object.values(state).filter(Boolean).length;
  const items = [
    { k: 'conf', label: 'Confusione mentale nuova' },
    { k: 'urea', label: 'Urea > 7 mmol/L (BUN > 19 mg/dL)' },
    { k: 'rr', label: 'Frequenza respiratoria >= 30/min' },
    { k: 'bp', label: 'PAS < 90 o PAD <= 60 mmHg' },
    { k: 'age', label: 'Eta >= 65 anni' },
  ];
  let tone = 'good';
  let interp = 'Basso rischio — gestione domiciliare (mortalita ~1%)';
  if (score >= 3) { tone = 'danger'; interp = 'ALTO rischio — ricovero, considerare UTI (mortalita 15-40%)'; }
  else if (score === 2) { tone = 'warning'; interp = 'Rischio intermedio — valutare ricovero (mortalita ~9%)'; }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2">
        {items.map((it) => (
          <button
            key={it.k}
            onClick={() => toggle(it.k)}
            className={`text-left rounded-lg border p-2 text-xs transition-colors ${state[it.k] ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:border-primary/40'}`}
            data-testid={`curb-${it.k}`}
          >
            {state[it.k] ? '✓ ' : ''}{it.label}
          </button>
        ))}
      </div>
      <ResultBox value={score} unit="/ 5" interpretation={interp} tone={tone} />
    </div>
  );
};

// ---------------- Dose pediatrica (mg/kg) ----------------
export const PediatricDoseCalc = () => {
  const [w, setW] = useState(null);
  const [dose, setDose] = useState(null);
  const total = w && dose ? w * dose : null;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <NumberInput id="ped-w" label="Peso paziente" unit="kg" value={w} onChange={setW} min={0.1} step={0.1} />
        <NumberInput id="ped-dose" label="Dose farmaco" unit="mg/kg" value={dose} onChange={setDose} min={0.01} step={0.1} />
      </div>
      <ResultBox value={total} unit="mg totali" interpretation="Verifica sempre il dosaggio massimo giornaliero e la posologia in scheda tecnica" tone="warning" />
    </div>
  );
};
